import { useId, useState } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { cn } from "@/lib/cn";

import IconButton from "../Button/IconButton";
import type { Person } from "../Combobox/PersonSelectorCombobox";
import PersonSelectorCombobox from "../Combobox/PersonSelectorCombobox";
import type { Category } from "../Combobox/PersonSelectorCombobox/-query/type";
import Icon from "../Icon/Icon";
import { Button } from "../ui/button";

type Props = {
  value?: Person;
  onChange: (option?: Person) => void;
  allowEmpty?: boolean;
  disabled?: boolean;
  placeHolder?: string;
  width?: number;
  disabledPersonIds?: Set<string>;
  /** カテゴリフィルタ（例: CATEGORY_FILTER.ALL_ALL で離籍者を除外） */
  category?: Category;
};

/**
 * Worker の選択肢に対応した Select
 */
export function WorkerSelect(props: Props) {
  const tc = useCommonTranslation();

  const [open, setOpen] = useState(false);

  const popoverContentId = useId();
  const canClear = Boolean(props.allowEmpty && !props.disabled && props.value);

  return (
    <Popover open={open} onOpenChange={() => setOpen(!open)}>
      <div
        className="relative inline-block"
        style={props.width ? { width: props.width } : undefined}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-controls={popoverContentId}
            className={cn(
              "relative w-full justify-start overflow-hidden text-ellipsis pr-10",
              !props.value && "text-muted-foreground",
            )}
            disabled={props.disabled}
          >
            <span className={cn("min-w-0 truncate", canClear && "pr-8")}>
              {props.value?.name || tc("pleaseSelect")}
            </span>
            <div className="absolute inset-y-0 right-3 flex items-center">
              <Icon
                iconName="ChevronDownIcon"
                className="shrink-0 opacity-50"
              />
            </div>
          </Button>
        </PopoverTrigger>

        {canClear && (
          <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton
              icon="XIcon"
              variant="mutedGhost"
              size="ExtraSmall"
              aria-label="Clear selection"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onChange(undefined);
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        )}
      </div>
      <PopoverContent
        id={popoverContentId}
        className="w-full overflow-hidden p-0"
        style={props.width ? { width: props.width } : undefined}
        align="start"
      >
        <PersonSelectorCombobox
          onSelect={(person) => {
            props.onChange(person);
            setOpen(false);
          }}
          disabledPersonIds={props.disabledPersonIds}
          onClose={() => setOpen(false)}
          category={props.category}
        />
      </PopoverContent>
    </Popover>
  );
}
